import React, { useState } from "react";
import axios from "axios";
import { z } from "zod";
import { UploadOutlined, PlusOutlined } from "@ant-design/icons";
import { UploadFile, Upload, Image } from "antd";
import { UploadChangeParam, UploadProps } from "antd/es/upload";
import { loadFileInfo } from "src/common/apiFiles";
import { apiFilesUrl } from "src/common/apiUrl";
import { AttrUploaderProps } from "src/common/attrEdit/components";
import { onError as onCommonError } from "src/common/onError";
import { classNames } from "src/common/classNames";
// eslint-disable-next-line import/no-extraneous-dependencies
import { UploadRequestOption } from "rc-upload/lib/interface";
import { RcFile } from "antd/lib/upload";
import { t } from "i18next";
import { UploaderModal } from "./UploaderModal";
import styles from "./PlmUploader.module.less";
import { urlForDownload } from "./imgSrc";
import { filterClipboardData } from "./filterClipboardData";
import { UploadInfo } from "./UploadInfo";
import { SolidButton } from "../buttons/SolidButton";

export interface PropsPlmUploaderRender {
  isImage: boolean;
  fileList: UploadFile[];
  uploadProps: UploadProps;
  disabled?: boolean;
  maxSize?: number;
  setIsModalOpen: (value: boolean) => void;
  handleRemove: (file: UploadFile) => void;
}

type PropsPlmUploader = AttrUploaderProps &
  Omit<UploadProps, "fileList" | "onChange"> & {
    value?: string[];
    onChange?(v: string[] | undefined): void;
    isImage: boolean;
    render?: (props: PropsPlmUploaderRender) => React.ReactNode;
  };

export const zUploadResult = z.object({
  fileName: z.string(),
  filePath: z.string(),
});
export type ZUploadResult = z.infer<typeof zUploadResult>;

export type TFilesDict = Record<string, UploadFile>;

export const makeFileItem = (
  isImage: boolean,
  uid: string,
  name?: string,
  size?: number,
): UploadFile => ({
  uid,
  size,
  name: name ?? uid,
  url: urlForDownload(isImage, uid, isImage ? "medium" : undefined),
  status: "done",
});

export type UploadMethods = {
  upload: ({ file, onSuccess, onError }: UploadRequestOption) => void;
  beforeUpload: (file: UploadFile, uploadFileList: UploadFile[]) => void;
};

export const onFileListChanged = async (
  newFileList: UploadFile[],
  onChange?: (v: string[] | undefined) => void,
) => {
  const newValues = newFileList
    .filter(({ status }) => status === "done")
    .map(({ uid }) => uid);
  onChange?.(newValues);
};

export const makeUploadMethods = (
  isImage: boolean,
  fileList: UploadFile[],
  maxSize?: number,
): UploadMethods => {
  const upload = async ({ file, onSuccess, onError }: UploadRequestOption) => {
    try {
      const resp = await axios.post(
        apiFilesUrl(""),
        { file },
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Accept: "*/*",
          },
          params: {
            fileType: isImage ? "IMAGE" : undefined,
          },
        },
      );
      const result = zUploadResult.parse(resp.data);
      if (onSuccess) {
        onSuccess(result);
      }
    } catch (error) {
      if (onError) {
        onError(error);
        onCommonError(error);
      }
    }
  };

  const calculateTotalSize = (files: UploadFile[]) =>
    files.reduce((total, file) => total + (file.size || 0), 0) / (1024 * 1024);

  const beforeUpload = async (
    file: UploadFile,
    uploadFileList: UploadFile[],
  ) => {
    const lastFileUid = uploadFileList[uploadFileList.length - 1]?.uid;
    try {
      if (maxSize) {
        const uploadTotalSize = calculateTotalSize([
          ...fileList,
          ...uploadFileList,
        ]);

        if (uploadTotalSize > maxSize) {
          throw Error(t("Total files size exceeds", { size: maxSize }));
        }
      }
      return true;
    } catch (error) {
      if (file.uid === lastFileUid) {
        onCommonError(error);
      }
      return Upload.LIST_IGNORE;
    }
  };

  return { upload, beforeUpload };
};

export const PlmUploader: React.FC<PropsPlmUploader> = (props) => {
  const {
    value,
    render,
    onChange,
    isImage,
    disabled,
    multiple,
    maxCount,
    maxSize,
    accept,
    ...uploadProps
  } = props;
  const [fileList, setFileList] = React.useState<UploadFile[]>([]);
  const [dict, setDict] = React.useState<TFilesDict>({});
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const uploadMaxCount = (multiple ? maxCount : undefined) ?? 1;
  React.useEffect(() => {
    const guids: string[] = value ?? [];
    setFileList((prev) => [
      ...prev.filter((x) => x.status === "error"),
      ...guids.map((uid) => {
        const file = dict[uid];
        return file?.url
          ? file
          : makeFileItem(isImage, uid, file?.name, file?.size);
      }),
    ]);
    guids
      .filter((uid) => !dict[uid])
      .map((uid) =>
        loadFileInfo(uid)
          .then(({ fileName, size }) => {
            const file: UploadFile = makeFileItem(isImage, uid, fileName, size);
            setDict((prev) => ({ ...prev, [uid]: file }));
            setFileList((prev) =>
              prev.map((item) => (item.uid === uid ? file : item)),
            );
          })
          .catch(onCommonError),
      );
  }, [value]);

  const { upload, beforeUpload } = makeUploadMethods(
    isImage,
    fileList,
    maxSize,
  );

  const handleChange = async (info: UploadChangeParam) => {
    const { status, response } = info.file;
    const curFileList = info.fileList;
    setFileList(curFileList);
    if (status === "done") {
      // eslint-disable-next-line no-param-reassign
      info.file.uid = response.filePath;
      setDict((prev) => ({
        ...prev,
        [response.filePath]: info.file as RcFile,
      }));
      setFileList(curFileList);
    }
    const isReadyForOnChange = curFileList.every(
      ({ status: itemStatus }) => itemStatus !== "uploading",
    );
    if (isReadyForOnChange) {
      await onFileListChanged(curFileList, onChange);
    }
  };

  const handleChangeOrder = (list: UploadFile[]) => {
    setFileList(list);
    onFileListChanged(list, onChange);
  };

  const handleRemove = (file: UploadFile) => {
    const newFileList = fileList.filter(({ uid }) => uid !== file.uid);
    setFileList(newFileList);
    onFileListChanged(
      fileList.filter(({ uid }) => uid !== file.uid),
      onChange,
    );
  };

  const handlePaste = async (e: ClipboardEvent) => {
    // Поскольку при вставке из буфера мы не можем пользоваться всем функционалом Upload,
    // приходится устанавливать состояния загрузки в fileList,
    // а также выполнять все необходимые проверки перед загрузкой файлов (accept, maxCount, maxSize) вручную
    const files = filterClipboardData(
      Array.from(e.clipboardData?.items || [])
        .map((item) => item.getAsFile())
        .filter(Boolean) as File[],
      uploadMaxCount,
      fileList,
      maxSize,
      accept,
    );

    const loadingFiles: UploadFile[] = files.map((file) => ({
      uid: `${file.name ?? ""}-loading`,
      name: "",
      status: "uploading",
    }));
    setFileList((prevFileList) => [...prevFileList, ...loadingFiles]);

    try {
      const newFiles = await Promise.all(
        files.map(
          (file) =>
            new Promise<UploadFile>((resolve) => {
              const uploadOptions: UploadRequestOption<ZUploadResult> = {
                file,
                onSuccess: (result) => {
                  const { filePath } = result;
                  const newFile: UploadFile = {
                    uid: filePath,
                    name: file.name,
                    status: "done",
                    url: urlForDownload(isImage, filePath),
                    originFileObj: file as RcFile,
                  };
                  setDict((prev) => ({ ...prev, [filePath]: newFile }));
                  resolve(newFile);
                },
                onError: () =>
                  resolve({
                    uid: `${file.name}-error`,
                    name: file.name,
                    status: "error",
                  }),
                action: apiFilesUrl(""),
                method: "POST",
              };
              upload(uploadOptions);
            }),
        ),
      );

      const updatedFileList = [...fileList, ...newFiles];
      setFileList(updatedFileList);
      onFileListChanged(updatedFileList, onChange);
    } catch (error) {
      onCommonError(error);
    }
  };

  React.useEffect(() => {
    if (isModalOpen) document.addEventListener("paste", handlePaste);
    return () => {
      document.removeEventListener("paste", handlePaste);
    };
  }, [fileList, isModalOpen]);

  const sharedUploadProps: UploadProps = {
    ...uploadProps,
    fileList,
    customRequest: async (options) => upload(options),
    onChange: handleChange,
    onRemove: handleRemove,
    disabled,
    maxCount: uploadMaxCount,
    multiple,
    beforeUpload,
    accept,
  };

  const renderProps: PropsPlmUploaderRender = {
    setIsModalOpen,
    fileList,
    uploadProps: sharedUploadProps,
    maxSize,
    isImage,
    disabled,
    handleRemove,
  };

  return (
    <>
      {render ? render(renderProps) : <DefaultInner {...renderProps} />}
      <UploaderModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onOrderChange={handleChangeOrder}
        onDelete={handleRemove}
        isImage={isImage}
        maxSize={maxSize}
        uploadProps={sharedUploadProps}
      />
    </>
  );
};

const DefaultInner: React.FC<PropsPlmUploaderRender> = ({
  isImage,
  disabled,
  fileList,
  uploadProps,
  setIsModalOpen,
  maxSize,
}) => {
  const { maxCount = 1, accept } = uploadProps;
  const [previewImageSrc, setPreviewImageSrc] = useState("");
  const handleOpenModal = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.stopPropagation();
    setIsModalOpen(true);
  };
  const onPreview = async (file: UploadFile) => {
    setPreviewImageSrc(urlForDownload(true, file.uid, "large"));
  };
  const imageUploadProps: UploadProps | undefined = isImage
    ? {
        listType: "picture-card",
        onPreview,
      }
    : undefined;
  return (
    <>
      <Upload
        className={classNames([
          [!isImage, styles.plmFileUploader],
          [
            fileList.length !== 0 && fileList.length < maxCount,
            styles.plmFileUploaderSpace,
          ],
          [fileList.length === 0, styles.plmImgUploaderStart],
        ])}
        {...uploadProps}
        {...imageUploadProps}
      >
        {(fileList.length === 0 || fileList.length < maxCount) &&
          (!isImage ? (
            <SolidButton
              disabled={disabled}
              className={styles.fileUploadButton}
              icon={<UploadOutlined />}
              onClick={handleOpenModal}
            >
              {t("Upload")}
            </SolidButton>
          ) : (
            <button
              disabled={disabled}
              className={styles.imageUploadButton}
              type="button"
              onClick={handleOpenModal}
            >
              <PlusOutlined />
              <div>{t("Upload")}</div>
            </button>
          ))}
      </Upload>
      {isImage && (
        <UploadInfo maxCount={maxCount} accept={accept} maxSize={maxSize} />
      )}
      {previewImageSrc && (
        <Image
          wrapperStyle={{ display: "none" }}
          preview={{
            visible: true,
            onVisibleChange: () => setPreviewImageSrc(""),
          }}
          src={previewImageSrc}
        />
      )}
    </>
  );
};
export { UploadInfo };
